import { useQuery, useQueryClient, UseQueryResult, QueryClient } from '@tanstack/react-query';
import { tempeFabricationClientWrapper } from './tempeFabricationClientWrapper';
import type {
  Product,
  GetProductListOutput,
  CreatePaymentIntentInput,
  PaymentIntentOutput,
  FindOrdersByEmailInput,
  FindOrdersByEmailOutput,
  FindOrderByShortOrderIdInput,
  OrderOutput,
  Order
} from './tempeFabricationClientWrapper';
import type { ProductOutput, GetProductListInput } from '@tempefab-model/tempe-fabrication-client';

/**
 * Query key factory for products
 * This ensures consistent query keys across the app
 */
export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (filter?: string, limit?: string) => [...productKeys.lists(), { filter, limit }] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: string) => [...productKeys.details(), id] as const,
};

/**
 * Query key factory for orders
 * This ensures consistent query keys across the app
 */
export const orderKeys = {
  all: ['orders'] as const,
  byEmail: (email: string, zipCode: string) => [...orderKeys.all, 'email', { email, zipCode }] as const,
  byShortId: (shortOrderId: string, email: string) => [...orderKeys.all, 'shortId', { shortOrderId, email }] as const,
  byId: (orderId: string) => [...orderKeys.all, 'id', orderId] as const,
};

/**
 * Hook to fetch a single product by ID with 24-hour caching
 * @param id - Product ID
 * @returns Query result with product data
 */
export const useProduct = (id: string): UseQueryResult<ProductOutput, Error> => {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: async () => {
      console.log('[CachedClient] Fetching product:', id);
      const result = await tempeFabricationClientWrapper.getProduct(id);
      console.log('[CachedClient] Product fetched from server:', id);
      return result;
    },
    enabled: !!id, // Only run if id is provided
    // staleTime: 24 * 60 * 60 * 1000, // 24 hours
    // gcTime: 24 * 60 * 60 * 1000, // 24 hours
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch all products with 24-hour caching
 * @param input - Optional filter and pagination parameters
 * @returns Query result with products list
 */
export const useProducts = (input?: GetProductListInput): UseQueryResult<GetProductListOutput, Error> => {
  return useQuery({
    queryKey: productKeys.list(input?.filter, input?.limit),
    queryFn: async () => {
      console.log('[CachedClient] Fetching products with filter:', input?.filter, 'limit:', input?.limit);
      const result = await tempeFabricationClientWrapper.getAllProducts(input || {});
      console.log('[CachedClient] Products fetched from server, count:', result.results?.length);
      return result;
    },
    // staleTime: 24 * 60 * 60 * 1000, // 24 hours
    // gcTime: 24 * 60 * 60 * 1000, // 24 hours
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get cache invalidation utilities
 * Use this to invalidate product cache when a product becomes unavailable
 */
export const useInvalidateProduct = () => {
  const queryClient = useQueryClient();

  /**
   * Invalidate a specific product's cache
   * @param productId - ID of the product to invalidate
   */
  const invalidateProduct = async (productId: string) => {
    console.log('[CachedClient] Invalidating cache for product:', productId);
    await queryClient.invalidateQueries({
      queryKey: productKeys.detail(productId),
    });
    console.log('[CachedClient] Cache invalidated for product:', productId);
  };

  /**
   * Invalidate all products cache
   */
  const invalidateAllProducts = async () => {
    console.log('[CachedClient] Invalidating all products cache');
    await queryClient.invalidateQueries({
      queryKey: productKeys.all,
    });
    console.log('[CachedClient] All products cache invalidated');
  };

  return {
    invalidateProduct,
    invalidateAllProducts,
  };
};

/**
 * Create a payment intent (not cached - this is a mutation)
 * @param input - Payment intent details
 * @returns Promise with payment intent output
 */
export const createPaymentIntent = async (input: CreatePaymentIntentInput): Promise<PaymentIntentOutput> => {
  return tempeFabricationClientWrapper.createPaymentIntent(input);
};

/**
 * Hook to fetch orders by email and shipping zip code with 5-minute caching
 * @param email - Customer email address
 * @param zipCode - Shipping ZIP code
 * @param enabled - Whether the query should run (default: true)
 * @returns Query result with orders list
 */
export const useOrdersByEmail = (
  email: string,
  zipCode: string,
  enabled: boolean = true
): UseQueryResult<FindOrdersByEmailOutput, Error> => {
  return useQuery({
    queryKey: orderKeys.byEmail(email, zipCode),
    queryFn: async () => {
      console.log('[CachedClient] Fetching orders by email:', email, 'zipCode:', zipCode);
      const result = await tempeFabricationClientWrapper.findOrdersByEmail({
        email,
        shippingZipcode: zipCode,
      });
      console.log('[CachedClient] Orders fetched from server, count:', result.results?.length);
      return result;
    },
    enabled: enabled && !!email && !!zipCode, // Only run if enabled and both params provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a specific order by short order ID with 5-minute caching
 * @param shortOrderId - Short order ID
 * @param email - Customer email address
 * @param enabled - Whether the query should run (default: true)
 * @returns Query result with order details
 */
export const useOrderByShortId = (
  shortOrderId: string,
  email: string,
  enabled: boolean = true
): UseQueryResult<OrderOutput, Error> => {
  return useQuery({
    queryKey: orderKeys.byShortId(shortOrderId, email),
    queryFn: async () => {
      console.log('[CachedClient] Fetching order by short ID:', shortOrderId, 'email:', email);
      const result = await tempeFabricationClientWrapper.findOrderByShortOrderId({
        shortOrderId,
        email,
      });
      console.log('[CachedClient] Order fetched from server:', shortOrderId);
      return result;
    },
    enabled: enabled && !!shortOrderId && !!email, // Only run if enabled and both params provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a specific order by UUID with 5-minute caching
 * @param orderId - Order UUID
 * @param enabled - Whether the query should run (default: true)
 * @returns Query result with order details
 */
export const useOrder = (
  orderId: string,
  enabled: boolean = true
): UseQueryResult<OrderOutput, Error> => {
  return useQuery({
    queryKey: orderKeys.byId(orderId),
    queryFn: async () => {
      console.log('[CachedClient] Fetching order by ID:', orderId);
      const result = await tempeFabricationClientWrapper.getOrder(orderId);
      console.log('[CachedClient] Order fetched from server:', orderId);
      return result;
    },
    enabled: enabled && !!orderId, // Only run if enabled and orderId provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Utility to invalidate product cache outside of React components
 * Useful for error handlers
 * @param queryClient - QueryClient instance
 * @param productId - ID of the product to invalidate
 */
export const invalidateProductCache = async (queryClient: QueryClient, productId: string) => {
  console.log('[CachedClient] Static invalidation for product:', productId);
  await queryClient.invalidateQueries({
    queryKey: productKeys.detail(productId),
  });
};

// Export types
export type {
  Product,
  ProductOutput,
  GetProductListOutput,
  CreatePaymentIntentInput,
  PaymentIntentOutput,
  FindOrdersByEmailInput,
  FindOrdersByEmailOutput,
  FindOrderByShortOrderIdInput,
  OrderOutput,
  Order
};
